import React, { useContext } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../contexts/AppContext';
import './ButtonGeneral.css';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger';
type ButtonSize = 'small' | 'medium' | 'large';
type ButtonType = 'button' | 'submit' | 'reset';

type Props = {
    onClick: () => void;
    title?: string;
    loading?: boolean;
    disabled?: boolean;
    locked?: boolean;
    errorMessage?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    type?: ButtonType;
    icon?: string;
    iconPosition?: 'left' | 'right';
    fullWidth?: boolean;
    className?: string;
    style?: React.CSSProperties;
};

const ButtonGeneral = ({
    onClick,
    title = '',
    loading = false,
    disabled = false,
    locked = false,
    errorMessage = 'Esta acción no está disponible.',
    variant = 'primary',
    size = 'medium',
    type = 'button',
    icon,
    iconPosition = 'left',
    fullWidth = false,
    className = '',
    style
}: Props) => {
    const { changeEventClick } = useContext(AppContext);

    const showLockedMessage = () => {
        toast.error(errorMessage);
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        
        if (changeEventClick) {
            changeEventClick(e);
        }
        
        if (locked) {
            showLockedMessage();
            return;
        }
        
        if (!loading && !disabled) {
            onClick();
        }
    };

    const getButtonClasses = () => {
        const baseClasses = 'table-action-btn';
        const variantClasses = {
            primary: '',
            secondary: 'secondary',
            outline: 'outline',
            danger: 'danger'
        };
        const sizeClasses = {
            small: 'small',
            medium: '',
            large: 'large'
        };
        
        let classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]}`;
        
        if (fullWidth) classes += ' full-width';
        if (loading || disabled) classes += ' disabled';
        if (className) classes += ` ${className}`;
        
        return classes.trim();
    };

    const renderIcon = () => {
        if (!icon) return null;
        
        return (
            <i className={`fas ${icon}`} />
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <>
                    <i className="fas fa-spinner fa-spin" />
                    {title && <span>Cargando...</span>}
                </>
            );
        }

        if (icon && title) {
            return (
                <>
                    {iconPosition === 'left' && renderIcon()}
                    <span>{title}</span>
                    {iconPosition === 'right' && renderIcon()}
                </>
            );
        }

        if (icon && !title) {
            return renderIcon();
        }

        return <span>{title}</span>;
    };

    return (
        <button
            type={type}
            className={getButtonClasses()}
            onClick={handleClick}
            disabled={loading || disabled}
            style={style}
        >
            {renderContent()}
        </button>
    );
};

export default ButtonGeneral;
